import { EnvironmentRibbon, ToastProvider, getFrontendConfigValue } from '@arnold/common';
import * as React from 'react';
import { Redirect, Route, BrowserRouter as Router, Switch, useLocation } from 'react-router-dom';
import { ApiVersionController } from './ApiVersionController';
import './App.css';
import ErrorBoundary from './ErrorBoundary';
import Admittance from './admittance/Admittance';
import ChatWindow from './chat/ChatWindow';
import { PrivateRouter } from './components';
import CookieConsent from './components/CookieConsent';
import PageLayout from './components/PageLayout';
import auth from './lib/auth';
import { getBrowserLanguages, useSaveSysAdminFromUrl } from './lib/common';
import ChatOverview from './overview/ChatOverviewPage';
import Overview from './overview/OverviewPage';
import Report from './reports/Report';
import ReportSequence from './reports/ReportSequence';
import NewReportPage from './reports/NewReportPage';
import i18n from './translations/i18n';
import UnsubscribeFromSummaryEmail from './unsubscribe/UnsubscribeFromSummaryEmail';
import { PageTitleAndFavicon } from './utils';
import SecureRespondentDetail from './reports/SecureRespondentDetail';

export const SUPPORTED_LANGUAGES = [
  'bg',
  'cs',
  'en',
  'hu',
  'pl',
  'ro',
  'sr',
  'ru',
  'sk',
  'es',
  'lt',
  'fi',
  'fr',
  'it',
  'uk',
  'mn',
  'de',
  'et',
  'hr',
  'mk',
  'bs',
];

type IState = {
  currentLanguage: string;
  allowedLanguages: string[];
  navbarContent: React.ReactNode | null;
  someReport: boolean;
};

class App extends React.Component<any, IState> {
  constructor(props: any) {
    super(props);
    this.state = {
      currentLanguage: '',
      allowedLanguages: [],
      navbarContent: null,
      someReport: false,
    };
    this.setLanguage();
  }

  setLanguage() {
    const savedLanguage = localStorage.getItem('i18nextLng');
    if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      this.setLocalization(savedLanguage);
      return;
    }
    const languages = getBrowserLanguages();
    const lang = languages
      .map((langCode) => langCode.substring(0, 2))
      .find((language) => language && SUPPORTED_LANGUAGES.includes(language));

    this.setLocalization(lang || 'en');
  }

  Chat = ({ match }: { match: any }) => (
    <PageLayout setLocalization={this.setLocalization} someReport={this.state.someReport} hideIntercomLauncher>
      <ChatWindow channelId={match.params.id} setSomeReport={this.setSomeReport} />
    </PageLayout>
  );

  SurveyChat = ({ match }: { match: any }) => (
    <PageLayout setLocalization={this.setLocalization} hideIntercomLauncher>
      {/* eslint-disable-next-line jsx-a11y/no-access-key */}
      <ChatWindow accessKey={match.params.accessKey} setSomeReport={this.setSomeReport} />
    </PageLayout>
  );

  Report = ({ match }: { match: any }) => {
    const location = useLocation();
    useSaveSysAdminFromUrl(match.params.token, location.search);
    return (
      <>
        <PageLayout
          setLocalization={this.setLocalization}
          allowedLanguages={this.state.allowedLanguages}
          initLanguage={this.state.currentLanguage || i18n.language}
          navbarContent={this.state.navbarContent}
        >
          <Report
            accessToken={match.params.token}
            selectedLanguage={this.state.currentLanguage || i18n.language}
            changeLanguage={this.setLocalization}
            setAllowedLanguages={this.setAllowedLanguages}
            allowedLanguages={this.state.allowedLanguages}
            setNavbarContent={this.setNavbarContent}
          />
        </PageLayout>
        <PageTitleAndFavicon favicon="report" />
      </>
    );
  };

  ReportSequence = ({ match }: { match: any }) => {
    const location = useLocation();
    useSaveSysAdminFromUrl(match.params.token, location.search);
    return (
      <>
        <PageLayout
          setLocalization={this.setLocalization}
          allowedLanguages={this.state.allowedLanguages}
          initLanguage={this.state.currentLanguage}
          navbarContent={this.state.navbarContent}
        >
          <ReportSequence
            accessToken={match.params.token}
            selectedLanguage={this.state.currentLanguage}
            changeLanguage={this.setLocalization}
            setAllowedLanguages={this.setAllowedLanguages}
            allowedLanguages={this.state.allowedLanguages}
            setNavbarContent={this.setNavbarContent}
          />
        </PageLayout>
        <PageTitleAndFavicon favicon="report" />
      </>
    );
  };

  RespondentDetail = ({ match }: { match: any }) => {
    const location = useLocation();
    useSaveSysAdminFromUrl(match.params.token, location.search);
    return (
      <>
        <PageLayout
          setLocalization={this.setLocalization}
          allowedLanguages={this.state.allowedLanguages}
          initLanguage={this.state.currentLanguage}
          navbarContent={this.state.navbarContent}
        >
          <SecureRespondentDetail
            accessToken={match.params.token}
            selectedLanguage={this.state.currentLanguage}
            changeLanguage={this.setLocalization}
            setAllowedLanguages={this.setAllowedLanguages}
            allowedLanguages={this.state.allowedLanguages}
          />
        </PageLayout>
        <PageTitleAndFavicon favicon="report" />
      </>
    );
  };

  // Unlike the other pages, the new report brings its own full-height shell: the left menu carries the branding
  // and navigation, so there is no shared PageLayout header above it.
  NewReportPage = ({ match }: { match: any }) => {
    const location = useLocation();
    useSaveSysAdminFromUrl(match.params.token, location.search);
    return (
      <>
        <NewReportPage
          accessToken={match.params.token}
          selectedLanguage={this.state.currentLanguage || i18n.language}
          setLocalization={this.setLocalization}
          setAllowedLanguages={this.setAllowedLanguages}
          allowedLanguages={this.state.allowedLanguages}
          setNavbarContent={this.setNavbarContent}
          initLanguage={this.state.currentLanguage || i18n.language}
        />
        <PageTitleAndFavicon favicon="report" />
      </>
    );
  };

  ChatOverview = () => (
    <PageLayout setLocalization={this.setLocalization} someReport={this.state.someReport}>
      <ChatOverview setSomeReport={this.setSomeReport} />
    </PageLayout>
  );

  RedirectToOverview = () => <Redirect to={`/overview`} />;

  setLocalization = (lang: string) => {
    this.setState({ currentLanguage: lang });
    i18n.changeLanguage(lang);
  };

  setAllowedLanguages = (languages: string[]) => {
    this.setState({ allowedLanguages: languages });
  };

  setNavbarContent = (content: React.ReactNode) => {
    this.setState({ navbarContent: content });
  };

  setSomeReport = (someReport: boolean) => {
    this.setState({ someReport });
  };

  withPageLayout = (Component: React.ComponentType<any>) => () => (
    <PageLayout setLocalization={this.setLocalization}>
      <Component />
    </PageLayout>
  );

  render() {
    return (
      <Router>
        <ApiVersionController>
          <EnvironmentRibbon env={getFrontendConfigValue('ENVIRONMENT')} />
          <ToastProvider>
            <ErrorBoundary>
              <Switch>
                <PrivateRouter setLocalization={this.setLocalization} path="/overview/:tab?" component={Overview} />
                <Route path="/chat-overview/:id/:tab?" component={this.ChatOverview} />
                <Route path="/report-overview/:id/:tab?" component={this.RedirectToOverview} />
                <Route path="/admittance/:lang/:surveyId" component={this.withPageLayout(Admittance)} />
                <Route path="/report/:token" component={this.Report} />
                <Route path="/newReport/:token" component={this.NewReportPage} />
                <Route
                  path="/unsubscribe-from-summary-email/:token"
                  component={this.withPageLayout(UnsubscribeFromSummaryEmail)}
                />
                <Route path="/report-sequence/:token" component={this.ReportSequence} />
                <Route path="/results/:token" component={this.Report} />
                <Route path="/process-results/:token" component={this.ReportSequence} />
                <Route path="/respondent-detail/:token" component={this.RespondentDetail} />
                <Route path="/surveys/:accessKey" component={this.SurveyChat} />
                <Route path="/:id" component={this.Chat} />
                {auth.isLoggedIn() && !window.location.search && <Redirect from={'/'} exact={true} to={'/overview'} />}
                <Route component={this.Chat} />
              </Switch>
            </ErrorBoundary>
          </ToastProvider>
          <CookieConsent />
        </ApiVersionController>
      </Router>
    );
  }
}

export default App;
